import React, { useEffect } from 'react';
import { useActor, useInterpret } from '@xstate/react';
import { Event, State, StateMachine } from 'xstate';
import { StateListener } from 'xstate/lib/interpreter';

import config from 'config';

export interface StepComponentProps {
  state: State<any>;
  send: (event: Event<any>) => void;
  isTransitionAllowed: (event: string) => boolean;
  standaloneMode?: boolean;
  id?: string;
}

type StateMeta = {
  component?: React.ComponentType<unknown>;
  props?: Record<string, unknown>;
};

type Args = Parameters<typeof useInterpret>;

interface Props<Context> {
  machine: StateMachine<Context, any, any, any, any, any, any>;
  context?: React.Context<unknown>;
  options: Args[1] & { context: Context };
  onTransition?: StateListener<Context, any>;
}

/**
 * Take an xState machine definition, and render whatever component there is in `currenState.meta.component`
 * We also pass it a bunch of useful things according to `StepComponentProps`
 */
export const RenderMachine = <Context,>({
  machine,
  options,
  context,
  onTransition,
}: Props<Context>) => {
  const service = useInterpret(machine, {
    devTools: !config.isProduction,
    ...options,
  });
  if (onTransition) {
    service.onTransition(onTransition);
  }
  const [state, send] = useActor(service);

  useEffect(() => {
    send({ type: 'UPDATE_CONTEXT', value: options.context });
  }, [options.context, send]);

  const findComponentMeta = (currentState: typeof state): React.ComponentType<unknown> | null => {
    const meta = Object.values(currentState.meta).find((m: StateMeta) => m.component);
    if (meta?.component) {
      return meta;
    }
    // eslint-disable-next-line no-restricted-syntax
    for (const childState of Object.values(currentState.children)) {
      if (childState.state) {
        const childMeta = findComponentMeta(childState.state);
        if (childMeta) {
          return childMeta;
        }
      }
    }

    return null;
  };

  const meta = findComponentMeta(state);
  const StepComponent = meta?.component;

  if (!StepComponent) {
    return null;
  }

  const isTransitionAllowed = (event: string) => {
    if (!state) {
      return [];
    }
    const nextState = machine.transition(state, event);
    return nextState.changed;
  };

  const stepProps = {
    ...(meta?.props || {}),
    state,
    send,
    isTransitionAllowed,
  };

  const step = <StepComponent {...stepProps} />;

  const ContextProvider = context?.Provider;

  if (ContextProvider) {
    return <ContextProvider value={stepProps}>{step}</ContextProvider>;
  }
  return step;
};
